import React from "react";
import { Progress, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from "@ant-design/icons";

const { Text } = Typography;

/**
 * Şifre güçlülük hesaplama
 * @param {string} password - Şifre
 * @param {number} minLength - Minimum uzunluk (varsayılan: 6)
 * @returns {number} Güçlülük puanı (0-100)
 */
export const calculatePasswordStrength = (password, minLength = 6) => {
  let strength = 0;
  if (password.length >= minLength) strength += 20;
  if (password.length >= minLength + 2) strength += 20;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 20;
  if (/\d/.test(password)) strength += 20;
  if (/[^A-Za-z0-9]/.test(password)) strength += 20;
  return strength;
};

/**
 * Şifre güçlülük durumu
 * @param {number} strength - Güçlülük puanı
 * @returns {{ status: string, text: string, color: string }}
 */
export const getPasswordStatus = (strength) => {
  if (strength <= 20) return { status: "exception", text: "Çok Zayıf", color: "#ff4d4f" };
  if (strength <= 40) return { status: "exception", text: "Zayıf", color: "#ff7875" };
  if (strength <= 60) return { status: "normal", text: "Orta", color: "#faad14" };
  if (strength <= 80) return { status: "success", text: "İyi", color: "#52c41a" };
  return { status: "success", text: "Güçlü", color: "#389e0d" };
};

/**
 * Şifre gereksinimleri oluştur
 * @param {string} password - Şifre
 * @param {number} minLength - Minimum uzunluk
 * @param {boolean} isCustomer - Müşteri için mi (daha sıkı kurallar)
 * @returns {Array<{ met: boolean, text: string, required: boolean }>}
 */
export const getPasswordRequirements = (password, minLength = 6, isCustomer = false) => {
  const requirements = [
    {
      met: password.length >= minLength,
      text: `En az ${minLength} karakter`,
      required: true,
    },
    {
      met: /\d/.test(password),
      text: "En az bir rakam",
      required: false,
    },
    {
      met: /[a-z]/.test(password) && /[A-Z]/.test(password),
      text: "Büyük ve küçük harf",
      required: false,
    },
    {
      met: /[^A-Za-z0-9]/.test(password),
      text: "Özel karakter (!@#$% vb.)",
      required: false,
    },
  ];

  if (isCustomer) {
    requirements.splice(1, 0, {
      met: password.length >= 12,
      text: "12+ karakter (önerilen)",
      required: false,
    });
  }

  return requirements;
};

/**
 * Şifre güçlülük göstergesi
 * @param {Object} props
 * @param {string} props.password - Şifre
 * @param {number} props.minLength - Minimum uzunluk
 * @param {boolean} props.isCustomer - Müşteri için mi
 * @param {boolean} props.showRequirements - Gereksinimleri göster
 */
const PasswordStrengthMeter = ({
  password,
  minLength = 6,
  isCustomer = false,
  showRequirements = true,
}) => {
  if (!password) return null;

  const strength = calculatePasswordStrength(password, minLength);
  const status = getPasswordStatus(strength);
  const requirements = getPasswordRequirements(password, minLength, isCustomer);

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 12, marginBottom: 4, display: "block" }}>
          Şifre Gücü:{" "}
          <span style={{ color: status.color, fontWeight: "bold" }}>{status.text}</span>
        </Text>
        <Progress
          percent={strength}
          status={status.status}
          showInfo={false}
          strokeColor={status.color}
        />
      </div>

      {showRequirements && (
        <div
          style={{
            background: "#f0f2f5",
            padding: 12,
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 8, display: "block" }}>
            Şifre Gereksinimleri:
          </Text>
          {requirements.map((req, index) => (
            <div key={index} style={{ marginBottom: 4 }}>
              {req.met ? (
                <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
              ) : req.required ? (
                <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
              ) : (
                <WarningOutlined style={{ color: "#faad14", marginRight: 8 }} />
              )}
              <Text
                style={{
                  fontSize: 12,
                  color: req.met ? "#52c41a" : req.required ? "#ff4d4f" : "#8c8c8c",
                  textDecoration: req.met ? "line-through" : "none",
                }}
              >
                {req.text}
                {req.required && !req.met && " *"}
              </Text>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default PasswordStrengthMeter;
